import { useEffect, useState, useCallback, useRef } from "react";
import { Box, Flex, Text, HStack, Stack, Button, Icon } from "@chakra-ui/react";
import { Globe, Target, Building2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/theme/colors";

// Super-Admin only: sets a master template's scope and, for Specialized
// templates, which client companies can see it. Writes save instantly.
export function TemplateVisibility({ templateId, scope: initialScope, flash }) {
  const { user, companies } = useAuth();
  const [scope, setScope] = useState(initialScope || "core");
  const [assigned, setAssigned] = useState([]);
  const [busy, setBusy] = useState(false);
  const flashRef = useRef(flash);
  flashRef.current = flash;

  const companyList = Object.values(companies)
    .filter((c) => c.active !== false)
    .sort((a, b) => a.name.localeCompare(b.name));

  const load = useCallback(async () => {
    const { data, error } = await supabase.from("template_assignments").select("company_id").eq("template_id", templateId);
    if (error) { flashRef.current("error", error.message); return; }
    setAssigned((data || []).map((r) => r.company_id));
  }, [templateId]);
  useEffect(() => { load(); }, [load]);

  const changeScope = async (next) => {
    if (next === scope || busy) return;
    setBusy(true);
    const { error } = await supabase.from("templates").update({ scope: next }).eq("id", templateId);
    setBusy(false);
    if (error) { flash("error", error.message); return; }
    setScope(next);
    flash("success", next === "core"
      ? "Scope set to Core — visible to every client company."
      : "Scope set to Specialized — visible only to the companies you assign below.");
  };

  const toggleCompany = async (cid) => {
    const on = assigned.includes(cid);
    const { error } = on
      ? await supabase.from("template_assignments").delete().eq("template_id", templateId).eq("company_id", cid)
      : await supabase.from("template_assignments").insert({ template_id: templateId, company_id: cid, assigned_by: user.id });
    if (error) { flash("error", error.message); return; }
    setAssigned((a) => (on ? a.filter((x) => x !== cid) : [...a, cid]));
  };

  const pill = (active) => ({
    size: "xs",
    variant: active ? "solid" : "outline",
    bg: active ? colors.teal : "transparent",
    color: active ? "white" : colors.textSecondary,
    borderColor: colors.border,
  });

  return (
    <Box bg={colors.white} border="1px solid" borderColor={colors.border} borderRadius="12px" p="4" mb="4" data-testid="template-visibility-panel">
      <Text fontSize="sm" fontWeight="600" color={colors.textPrimary} mb="2">Client visibility</Text>
      <HStack gap="2" flexWrap="wrap">
        <Button {...pill(scope === "core")} onClick={() => changeScope("core")} data-testid="scope-core-btn">
          <Globe size={13} /> Core — all companies
        </Button>
        <Button {...pill(scope === "specialized")} onClick={() => changeScope("specialized")} data-testid="scope-specialized-btn">
          <Target size={13} /> Specialized — assigned companies only
        </Button>
      </HStack>

      {scope === "specialized" && (
        <Box mt="3" pt="3" borderTop="1px solid" borderColor={colors.border}>
          {companyList.length === 0 ? (
            <Text fontSize="sm" color={colors.textSecondary}>No client companies yet.</Text>
          ) : (
            <Stack gap="1.5">
              {companyList.map((c) => (
                <HStack key={c.id} as="label" gap="2" cursor="pointer">
                  <Box as="input" type="checkbox" checked={assigned.includes(c.id)} onChange={() => toggleCompany(c.id)}
                    accentColor={colors.teal} w="15px" h="15px" data-testid={`assign-company-${c.id}`} />
                  <Icon as={Building2} boxSize="3.5" color={colors.textSecondary} />
                  <Text fontSize="sm" color={colors.textPrimary}>{c.name}</Text>
                </HStack>
              ))}
            </Stack>
          )}
          {assigned.length === 0 && (
            <Flex mt="2">
              <Text fontSize="xs" color={colors.poppy} data-testid="no-assignments-warning">
                Not assigned to any company yet — no client can see this template.
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Box>
  );
}
